import React from "react";
import {getArticleData, pingOnPdfDownload} from "../../api/articleData";
import dateformat from "dateformat";
import {PDFExport} from "@progress/kendo-react-pdf";
import sanitize from "sanitize-filename";
import {ceil, get, isNumber, preventURLWebLink} from '../../util'

class Home extends React.Component {
    constructor(props) {
        super(props);
        const shouldDownload = !!props.match.params.downloadParam;
        this.bindMethods();
        this.pdfCanvasReference = React.createRef();

        this.state = {
            response: null,
            backToId: this.getBackToIdFromUrl(),
            isPdfDownloading: false,
            shouldDownload: shouldDownload,
        };
    }

    componentDidMount() {
        const encryptedBlob = this.getArticleDataFromUrl();

        getArticleData(encryptedBlob)
            .then((result) => {
                this.setState(
                    {
                        response: result,
                    },
                    () => {
                        if (this.state.shouldDownload) {
                            this.downloadPdf();
                            this.setState({
                                ...this.state,
                                shouldDownload: false,
                            });
                        }
                    }
                );
            })
            .catch((error) => {
                this.setState({
                    response: {
                        errorMessage: error,
                    },
                });
            });
    }

    /**
     * Get the article data from the URL
     * @returns {*}
     */
    getArticleDataFromUrl() {
        const { match } = this.props;
        const params = match ? match.params : null;
        if (params && params.articleData) {
            return params.articleData;
        } else {
            return null;
        }
    }

    /**
     * Get the ID from the URL to jump back to (when going back to view on web link)
     * @returns {*}
     */
    getBackToIdFromUrl() {
        const { match } = this.props;
        const params = match ? match.params : null;
        if (params && params.backToId) {
            return params.backToId;
        } else {
            return null;
        }
    }

    /**
     * Bind the various handlers to make sure they are attached to this class
     */
    bindMethods() {
        this.onButtonClick = this.onButtonClick.bind(this);
        this.downloadPdf = this.downloadPdf.bind(this);
    }

    /**
     * Button click handler
     * @param {SyntheticEvent} e - Look at https://reactjs.org/docs/events.html
     */
    onButtonClick(e) {
        // This is how we redirect in code
        this.props.history.push("/c2");
    }

    /**
     * Trigger a PDF download for the current article
     * @param event
     */
    downloadPdf(event) {
        if (event) {
            event.preventDefault();
        }
        if (this.state.isPdfDownloading) {
            // actually download
            this.pdfCanvasReference.current.save();

            pingOnPdfDownload(this.state.response.articleId)
                .then((result) => {
                    console.log("Ping PDF success");
                })
                .catch((error) => {
                    console.error("Error pinging PDF download", error);
                });

            this.setState({
                ...this.state,
                isPdfDownloading: false,
            });
        } else {
            this.setState(
                {
                    ...this.state,
                    isPdfDownloading: true,
                },
                () => {
                    this.downloadPdf();
                }
            );
        }
    }

    /**
     * Render the component based on the state and props (will be called every time state changes with setState)
     * @returns {*}
     */
    render() {
        const r = this.state.response || {};
        const haveArticle = !!(r && r.title);
        const { title, outletName } = r;
        return (
            <div className="pageContent">
                {this.getHeaderElement(r)}
                {this.getPageContent(r)}
                {this.state.isPdfDownloading && (
                    <PDFExport
                        paperSize="Letter"
                        fileName={sanitize(`${outletName}_${title}.pdf`)}
                        scale={0.6}
                        margin="2cm"
                        title=""
                        subject=""
                        keywords=""
                        ref={this.pdfCanvasReference}
                    >
                        {this.state.isPdfDownloading && (
                            <div
                                style={{
                                    padding: "15px",
                                    boxShadow: "5px 5px 5px black",
                                    height: "3300px",
                                    margin: "24px auto",
                                    display: "fixed",
                                    overflowX: "hidden",
                                    overflowY: "hidden",
                                }}
                            >
                                {haveArticle && this.getArticleElementForPDF(r)}
                            </div>
                        )}
                    </PDFExport>
                )}
            </div>
        );
    }

    /**
     * Return the page header element based on the response
     * @param response
     * @returns {Symbol(react.element)}
     */
    getHeaderElement(response) {
        const { vowUrl } = response;
        const { backToId } = this.state;
        const viewOnWebUrl = backToId ? `${vowUrl}#${backToId}` : vowUrl;
        return (
            <div className="pageHeader d-flex align-items-center">
                <div className="container d-flex align-items-center">
                    <div className="text-left">
                        {vowUrl && (
                            <a href={viewOnWebUrl} className="d-print-none">
                                <i className="fa fa-angle-double-left"></i>
                                &nbsp;Back to Newsletter
                            </a>
                        )}
                    </div>
                    <div className="logo text-right ml-auto">
                        <img src="images/2022LogoWhite.svg" />
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Return the page content based on the reponse (article content or error)
     * @param response
     * @returns {Symbol(react.element)}
     */
    getPageContent(response) {
        const r = this.state.response || {};
        const haveArticle = !!(r && r.title);
        return (
            <div className="articleContent container pt-2">
                {haveArticle
                    ? this.getArticleElement(r)
                    : this.getErrorElement(r)}
            </div>
        );
    }

    /**
     Return the article content element for the PDF based on the response
     * @param response
     * @returns {Symbol(react.element)}
     */
    getArticleElementForPDF(response) {
        const {
            articleTimestamp,
            body,
            brandingData,
            byline,
            outletName,
            title,
            arrowDirection,
            trendingScore,
            syndication,
            impressions,
            significance,
            outletPower,
            tone,
            reach,
            articleShares
        } = response;
        return (
            <div className="articleData pdf">
                {this.getAuthorOutletDateElement(
                    outletName,
                    byline,
                    articleTimestamp
                )}
                {this.getTitleElement(title)}
                {this.getTopBrandingElement(brandingData, true)}
                {this.getBodyElement(body)}
                {this.getMetadataElement({
                    tone,
                    arrowDirection,
                    trendingScore,
                    syndication,
                    impressions,
                    significance,
                    outletPower,
                    reach,
                    articleShares
                })}
                {this.getBottomBrandingElement(brandingData, true)}
            </div>
        );
    }

    /**
     * Get the metadata react component
     */
    getMetadataElement({
        arrowDirection,
        trendingScore,
        syndication,
        impressions,
        significance,
        outletPower,
        tone,
        reach,
       articleShares
    }) {
        const metrics = [];
        const includeTone = isNumber(tone);
        const includeSignificance = isNumber(significance);
        const includeOutletPower = isNumber(outletPower) && outletPower > 0;
        const includeImpressions = isNumber(impressions) && impressions > 0;
        const includeSyndication = isNumber(syndication) && syndication > 0;
        const includeArticleShares = isNumber(articleShares) && articleShares > 0;
        const includeReach = isNumber(reach) && reach > 0;
        const includeTrendingScore =
            isNumber(trendingScore) && trendingScore > 0;
        const includeArrowDirection = isNumber(arrowDirection);

        const toneMap = {
            "-2": {
                icon: "— —",
                text: "Very Negative",
                className: "negative",
            },
            "-1": {
                icon: "—",
                text: "Negative",
                className: "negative",
            },
            0: {
                icon: "o",
                text: "Neutral",
                className: "neutral",
            },
            1: {
                icon: "+",
                text: "Positive",
                className: "positive",
            },
            2: {
                icon: "+ +",
                text: "Very Positive",
                className: "positive",
            },
        };
        if (includeTone) {
            metrics.push(
                <div className="metric col-12 col-md-4" key="toneIcon">
                    <span className="metric-label col">Tone:</span>
                    <span
                        className={`metric-value col text-right icon-size tone ${get(
                            toneMap[tone],
                            "className"
                        )}`}
                    >
                        {get(toneMap[tone], "icon")}
                    </span>
                </div>
            );
        }
        if (includeSignificance) {
            const significanceTextMap = {
                0: "Low",
                1: "Medium",
                2: "High",
            };
            metrics.push(
                <div className="metric col-12 col-md-4" key="significance">
                    <span className="metric-label col">Significance:</span>
                    <span className="metric-value col text-right">
                        {significanceTextMap[significance]}
                    </span>
                </div>
            );
        }
        if (includeOutletPower) {
            const leftColor = "limegreen";
            const rightColor = "gray";
            const width = 1;
            const heights = [0.25, 0.5, 0.75, 1];
            const output = [];
            const index = 1;
            heights.forEach((height, index) => {
                let color = leftColor;
                if ((outletPower - 1) < index) {
                    color = rightColor;
                }
                output.push(
                    <div
                        style={{
                            backgroundColor: color,
                            height: `${height * width}rem`,
                            width: `${width}rem`,
                        }}
                        key={`power_${index}`}
                        className="outletPowerBar"
                    ></div>
                );
            });
            metrics.push(
                <div className="metric col-12 col-md-4" key="outletPower">
                    <span className="metric-label col">Outlet Power:</span>
                    <span className="metric-value col text-right outletPower">
                        {output}
                    </span>
                </div>
            );
        }

        if (includeReach) {
            metrics.push(
                <div className="metric col-12 col-md-4" key="impressions">
                    <span className="metric-label col">Potential Impressions:</span>
                    <span className="metric-value col text-right">
                        {this.formatNumber(reach)}
                    </span>
                </div>
            );
        }

        if (includeImpressions) {
            metrics.push(
                <div className="metric col-12 col-md-4" key="impressions">
                    <span className="metric-label col">Outlet Reach:</span>
                    <span className="metric-value col text-right">
                        {this.formatNumber(impressions)}
                    </span>
                </div>
            );
        }
        if (includeSyndication) {
            metrics.push(
                <div className="metric col-12 col-md-4" key="syndication">
                    <span className="metric-label col">Syndication:</span>
                    <span className="metric-value col text-right">
                        {this.formatNumber(syndication)}
                    </span>
                </div>
            );
        }
        if (includeArticleShares) {
            metrics.push(
                <div className="metric col-12 col-md-4" key="syndication">
                    <span className="metric-label col">Shares:</span>
                    <span className="metric-value col text-right">
                        {this.formatNumber(articleShares)}
                    </span>
                </div>
            );
        }
        if (includeTrendingScore) {
            const arrowClassMap = {
                0: "",
                1: "rotate-ccw-45",
                2: "rotate-ccw-90",
                3: "rotate-ccw-135",
                4: "rotate-ccw-180",
            };
            metrics.push(
                <div className="metric col-12 col-md-4" key="trendingScore">
                    <span className="metric-label col">Trending Score:</span>
                    <span className="metric-value col text-right trendingScore">
                        <div>{this.formatNumber(trendingScore)}</div>
                        <div>
                            {includeArrowDirection && (
                                <div
                                    className={`p-0 m-0 ml-2 d-flex items-center`}
                                >
                                    <img
                                        src="images/arrow_down.png"
                                        className={
                                            arrowClassMap[arrowDirection]
                                        }
                                        style={{
                                            height: "1rem",
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </span>
                </div>
            );
        }

        const numTriads = ceil(metrics.length / 3);
        const triads = [];
        let metricIndex = 0;
        for (let triadIndex = 0; triadIndex < numTriads; triadIndex++) {
            triads.push(
                <div
                    className="row pb-0 pb-md-2 align-items-baseline"
                    key={triadIndex}
                >
                    {metrics[metricIndex] && metrics[metricIndex]}
                    {metrics[metricIndex + 1] && metrics[metricIndex + 1]}
                    {metrics[metricIndex + 2] && metrics[metricIndex + 2]}
                </div>
            );
            metricIndex += 3;
        }
        return <div className="article-metrics">{triads}</div>;
    }

    /**
     * Return the article content element based on the response
     * @param response
     * @returns {Symbol(react.element)}
     */
    getArticleElement(response) {
        const {
            articleTimestamp,
            body,
            brandingData,
            byline,
            excerpt,
            outletName,
            title,
            url,
        } = response;
        return (
            <div className="articleData">
                {this.getTitleElement(title)}
                {this.getAuthorOutletDateElement(
                    outletName,
                    byline,
                    articleTimestamp
                )}
                {this.getExcerptElement(excerpt)}
                {this.getTopBrandingElement(brandingData)}
                {this.getBodyElement(body)}
                {this.getUrlElement(url, brandingData)}
                {this.getPdfFDownloadElement()}
                {this.getBottomBrandingElement(brandingData)}
            </div>
        );
    }

    /**
     * Format a number to the usual format e.g. 1.3B for 1,300,000,000
     * @param {number} number
     * @returns {string}
     */
    formatNumber(number) {
        let stringValue;
        if (number < 1000) {
            // (0 - 1k)
            stringValue = number.toLocaleString();
        } else if (number < 1000000) {
            // (1k - 1m)
            stringValue = (number / 1000).toFixed(1).toLocaleString() + "K";
        } else if (number < 1000000000) {
            // (1m - 1b)
            stringValue = (number / 1000000).toFixed(1).toLocaleString() + "M";
        } else {
            // (1b +)
            stringValue =
                (number / 1000000000).toFixed(1).toLocaleString() + "B";
        }
        return stringValue;
    }

    /**
     * Return the error content element based on the response
     * @param response
     * @returns {Symbol(react.element)}
     */
    getErrorElement(response) {
        const { errorMessage } = response;
        if (errorMessage) {
            return <div className="errorMessage">{errorMessage}</div>;
        }
    }

    /**
     * Return the title if available
     * @param title
     * @returns {Symbol(react.element)}
     */
    getTitleElement(title) {
        if (title) {
            return <h3>{title}</h3>;
        }
    }

    /**
     * Return the outletAuthorDate if available
     * @param {string} outletName
     * @param {string} byLine
     * @param {timestamp} date
     * @returns {Symbol(react.element)}
     */
    getAuthorOutletDateElement(outletName, byline, date) {
        let string = "";
        const haveAnyInfo = !!(outletName || byline || date);
        if (haveAnyInfo) {
            if (outletName) {
                string += outletName;
            }
            if (byline) {
                string += `, ${byline}`;
            }
            if (date) {
                string += ` - ${dateformat(date, "dddd, mmmm dS, yyyy")}`;
            }
            return <p>{string}</p>;
        }
    }

    /**
     * Return the excerpt if available
     * @param excerpt
     * @returns {Symbol(react.element)}
     */
    getExcerptElement(excerpt) {
        if (excerpt) {
            return (
                <div>
                    <hr />
                    <p>Key Excerpt:</p>
                    <p>{excerpt}</p>
                    <hr />
                </div>
            );
        }
    }

    /**
     * Return the top branding if available
     * @param brandingData
     * @param replaceCopyrightIcon
     * @returns {Symbol(react.element)}
     */
    getTopBrandingElement(brandingData, replaceCopyrightIcon) {
        if (brandingData) {
            const { bestLicenseText, copyrightNote, secondaryCopyrightText } =
                brandingData;
            let stringArray = [];

            if (bestLicenseText) {
                stringArray.push(`Via ${bestLicenseText}`);
            }
            if (copyrightNote) {
                stringArray.push(copyrightNote);
            }
            if (secondaryCopyrightText) {
                if (replaceCopyrightIcon) {
                    let secondCopyrightText = secondaryCopyrightText.replace(/©|&copy;/gi, "Copyright");
                    stringArray.push(secondCopyrightText);
                } else {
                    stringArray.push(secondaryCopyrightText);
                }
            }

            return <p className="topBranding">{stringArray.join(" - ")}</p>;
        }
    }

    /**
     * Return the body if available
     * @param body
     * @returns {Symbol(react.element)}
     */
    getBodyElement(body) {
        if (body) {
            return body
                .split("\n")
                .map((item, index) => (
                    <p key={`body_index_${index}`}>{item}</p>
                ));
        }
    }

    /**
     * Return the url if available
     * @param url
     * @param brandingData
     * @returns {Symbol(react.element)}
     */
    getUrlElement(url, brandingData) {
        const { allowWebView } = brandingData;
        // const urlLabel = allowWebView ? "View on Web" : "View in System";
        if (allowWebView && url && !preventURLWebLink(url)) {
            return (
                <p>
                    <a href={url} target="_blank">View Online</a>
                </p>
            );
        }
    }

    /**
     * Return the pdf download button
     * @param url
     * @returns {Symbol(react.element)}
     */
    getPdfFDownloadElement() {
        return (
            <React.Fragment>
                <a
                    target="_blank"
                    href="#"
                    rel="noopener noreferrer"
                    style={{ cursor: "pointer" }}
                    onClick={(event) => this.downloadPdf(event)}
                >
                    <i className="fa fa-download" /> Download PDF
                </a>
                {this.state.isPdfDownloading && (
                    <a href="#">
                        <i className="fa fa-spin fa-circle-o-notch"></i>
                    </a>
                )}
            </React.Fragment>
        );
    }

    /**
     * Return the bottom branding if available
     * @param brandingData
     * @param replaceCopyrightIcon
     * @returns {Symbol(react.element)}
     */
    getBottomBrandingElement(brandingData, replaceCopyrightIcon) {
        if (brandingData) {
            const {
                termsOfUseHref,
                brandingCopyrightLink,
                brandingCopyrightText,
                copyrightTextBlob,
                copy1Url,
                copy1UrlAnchorText,
                bestLicenseText,
            } = brandingData;
            const haveBranding = !!(
                termsOfUseHref ||
                brandingCopyrightText ||
                copyrightTextBlob
            );

            if (haveBranding) {
                let copyAnchorText = copy1UrlAnchorText;
                if (replaceCopyrightIcon && copy1UrlAnchorText) {
                    copyAnchorText = copy1UrlAnchorText.replace(/©|&copy;/gi, "Copyright");
                }
                const copy1UrlComponent = (
                    <a
                        href={copy1Url || ""}
                        dangerouslySetInnerHTML={{
                            __html: copyAnchorText,
                        }}
                    />
                );
                const isBloomberg = bestLicenseText === "Bloomberg";
                const copyrightTextStyle = isBloomberg
                    ? { marginRight: 4 }
                    : { marginLeft: 4 };
                let copyrightTextToDisplay = copyrightTextBlob;
                if (copy1UrlAnchorText && copyrightTextBlob) {
                    copyrightTextToDisplay = copyrightTextBlob.replace(
                        copy1UrlAnchorText,
                        ""
                    );
                }
                if (replaceCopyrightIcon && copyrightTextToDisplay) {
                    copyrightTextToDisplay = copyrightTextToDisplay.replace(/©|&copy;/gi, "Copyright");
                }

                return (
                    <div className="bottomBranding">
                        {termsOfUseHref && (
                            <div key="termsOfUseHref">
                                <a href={termsOfUseHref}>Terms of Use</a>
                            </div>
                        )}
                        {brandingCopyrightLink && (
                            <div key="brandingCopyrightLink">
                                <a
                                    href={brandingCopyrightLink}
                                    dangerouslySetInnerHTML={{
                                        __html: brandingCopyrightText,
                                    }}
                                />
                            </div>
                        )}
                        <div>
                            {!!copy1Url && !isBloomberg && copy1UrlComponent}
                            {copyrightTextToDisplay && (
                                <span
                                    key="copyrightTextBlob"
                                    dangerouslySetInnerHTML={{
                                        __html: copyrightTextToDisplay,
                                    }}
                                    style={
                                        copy1Url
                                            ? copyrightTextStyle
                                            : undefined
                                    }
                                />
                            )}
                            {!!copy1Url && isBloomberg && copy1UrlComponent}
                        </div>
                    </div>
                );
            }
        }
    }
}

export default Home;
